import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { detectCheckType } from "@/lib/agreement";

// ============================================================================
// AGR-02 — expiring-agreements renewal worklist. Read-only from
// mirror_serv_agr (active agreements whose expire_date falls within a window),
// enriched with the customer name. The retention play: work the list, renew.
// ============================================================================

export interface ExpiringAgreement {
  custno: string;
  locno: string;
  agrmt_no: string;
  agrmt_type: string | null;
  contr_per: string | null;
  contr_amt: number | null;
  expire_date: string;
  renew_date: string | null;
  last_srvc: string | null;
  customer_fullname: string | null;
  city: string | null;
  days_left: number;
}

function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function addDaysISO(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * Page through a PostgREST query in 1000-row chunks. Supabase's API caps every
 * single response at 1000 rows NO MATTER what .limit() asks for — a worklist
 * that trusts one request silently truncates (the Upcoming tab sat at exactly
 * "1000" because of this; same cap that bit the Podium sync). `build` must
 * return a FRESH query each call (builders are mutable) with a stable order.
 * (AUDIT-2026-06-09 A3.)
 */
async function pagedAll<T>(
  build: () => PromiseLike<{ data: unknown; error: { message: string } | null }> & {
    range: (from: number, to: number) => PromiseLike<{ data: unknown; error: { message: string } | null }>;
  },
  hardCap = 20000,
): Promise<T[]> {
  const out: T[] = [];
  const page = 1000;
  for (let from = 0; out.length < hardCap; from += page) {
    const { data, error } = await build().range(from, from + page - 1);
    if (error) throw new Error(error.message);
    const batch = (data ?? []) as T[];
    out.push(...batch);
    if (batch.length < page) break;
  }
  return out;
}

// Shared enrichment: map custno -> fullname. Chunked so a worklist of >1000
// customers can't overflow the .in() URL or the 1000-row response cap.
async function fetchNameMap(custnos: string[]): Promise<Map<string, string | null>> {
  const m = new Map<string, string | null>();
  for (let i = 0; i < custnos.length; i += 500) {
    const chunk = custnos.slice(i, i + 500);
    const { data, error } = await supabase
      .from("mirror_customer").select("custno, fullname").in("custno", chunk);
    if (error) throw error;
    for (const c of (data ?? []) as { custno: string; fullname: string | null }[]) {
      m.set(c.custno, c.fullname);
    }
  }
  return m;
}

// Shared enrichment: map (custno|locno) -> service city, for the zone filter.
// Chunked + paged: a 500-customer chunk can exceed 1000 location rows.
async function fetchCityMap(custnos: string[]): Promise<Map<string, string | null>> {
  const m = new Map<string, string | null>();
  for (let i = 0; i < custnos.length; i += 500) {
    const chunk = custnos.slice(i, i + 500);
    const rows = await pagedAll<{ custno: string; locno: string; city: string | null }>(
      () =>
        supabase
          .from("mirror_location")
          .select("custno, locno, city")
          .in("custno", chunk)
          .order("custno", { ascending: true })
          .order("locno", { ascending: true }),
    );
    for (const l of rows) m.set(`${l.custno}|${l.locno}`, l.city);
  }
  return m;
}

// ----------------------------------------------------------------------------
// Customer-level renewal signal — powers the renewal attention-chip on the
// Customer 360 header. We look at the customer's LATEST-expiring active
// agreement (their actual coverage horizon), not the earliest, so an old
// still-marked-active record doesn't masquerade as "expiring". The chip only
// surfaces when that horizon needs attention (expiring soon / lapsed).
// ----------------------------------------------------------------------------
export interface CustomerRenewal {
  agrmt_no: string;
  expire_date: string;
  days_left: number;
}

export function useCustomerAgreementRenewal(custno: string | undefined) {
  return useQuery({
    queryKey: ["customer_renewal", custno],
    enabled: !!custno,
    queryFn: async (): Promise<CustomerRenewal | null> => {
      // BOTH membership sources count (Seth 2026-08-11: his Pegasus-sold PLUS
      // plan was invisible to the 360 ribbon, the Overview membership card, and
      // the dispatch modal's member pricing — this one hook feeds them all).
      // ESC-mirrored agreements AND Pegasus-native plan sales; latest expiry wins.
      // MEMBERSHIP-ESC-FULFILLED: an ESC agreement the office set inactive after
      // its final tune-up is still a paid membership until its expire date, so
      // the candidates are the active rows PLUS the one `v_membership_plan`
      // calls 'fulfilled' (the SQL rule — the client cannot judge that itself).
      const [{ data: esc, error: e1 }, { data: peg, error: e2 }, { data: ful }] = await Promise.all([
        supabase
          .from("mirror_serv_agr")
          .select("agrmt_no, expire_date, sa_inactive")
          .eq("custno", custno!)
          .or(`sa_inactive.eq.0,expire_date.gte.${todayISO()}`)
          .not("expire_date", "is", null)
          .order("expire_date", { ascending: false })
          .limit(6),
        supabase
          .from("pegasus_plan_sale")
          .select("id, expire_date")
          .eq("custno", custno!)
          // MEMBER-TRUTH-01 (Seth 2026-10-20: "badge shows Member but not on a
          // plan"): only a SOLD plan is a membership — never a saved quote
          // (status 'quote'), a cancelled one, or one removed as made-in-error.
          .eq("status", "sold")
          .not("expire_date", "is", null)
          .order("expire_date", { ascending: false })
          .limit(1),
        supabase
          .from("v_membership_plan")
          .select("agrmt_no")
          .eq("custno", custno!)
          .eq("source", "esc")
          .eq("status", "fulfilled")
          .order("expire_date", { ascending: false })
          .limit(1),
      ]);
      if (e1) throw e1;
      if (e2) throw e2;
      const fulfilledNo = ((ful ?? [])[0] as { agrmt_no: string } | undefined)?.agrmt_no ?? null;
      const escRow = ((esc ?? []) as { agrmt_no: string; expire_date: string; sa_inactive: number | null }[])
        .find((r) => r.sa_inactive === 0 || r.agrmt_no === fulfilledNo);
      const pegRow = (peg ?? [])[0] as { id: string; expire_date: string } | undefined;
      let row: { agrmt_no: string; expire_date: string } | null = null;
      if (escRow && pegRow) {
        row = pegRow.expire_date > escRow.expire_date ? { agrmt_no: "PLUS (Pegasus)", expire_date: pegRow.expire_date } : escRow;
      } else if (pegRow) {
        row = { agrmt_no: "PLUS (Pegasus)", expire_date: pegRow.expire_date };
      } else if (escRow) {
        row = escRow;
      }
      if (!row) return null;
      const t = new Date(todayISO() + "T00:00:00").getTime();
      const days_left = Math.round(
        (new Date(row.expire_date + "T00:00:00").getTime() - t) / 86400000,
      );
      return { agrmt_no: row.agrmt_no, expire_date: row.expire_date, days_left };
    },
    staleTime: 5 * 60 * 1000,
  });
}

// ----------------------------------------------------------------------------
// MEMBERSHIP-CARD-01 (facelift pass 1, Seth 2026-08-18: "Membership box
// doesn't really tell me enough info to really grasp what kind of plus
// customer this is") — one normalized read for the 360 Overview card, rich
// enough to answer that at a glance: WHICH plan, visits used, member since,
// price, when it runs out. Sources both membership systems (ESC mirror +
// Pegasus-native plan sales); the later-expiring active one wins, same rule
// as useCustomerAgreementRenewal so the two never disagree.
// ----------------------------------------------------------------------------
/** MEMBERSHIPS-03: the one plan status every screen reads (SQL
 *  `pegasus_membership_status`). 'ended' = the term is over but a check it
 *  granted is still deliverable; 'lapsed' = over, nothing owed — the real
 *  win-back list. 'fulfilled' (MEMBERSHIP-ESC-FULFILLED, Seth 2026-11-03) =
 *  an ESC agreement the office set inactive AFTER its final tune-up: paid
 *  through its expire date, every check used, nothing owed — still a member,
 *  and the renewal conversation is now. ESC has no such state (an active
 *  agreement keeps generating unpaid tune-ups), so the office inactivates;
 *  before this it read as "cancelled". */
export type PlanLifecycle = "active" | "expiring" | "fulfilled" | "ended" | "lapsed" | "expired" | "renewed" | "cancelled";
/** The lifecycles that still mean "a member today" (plan in force or a check still owed). */
export const LIVE_LIFECYCLES: ReadonlyArray<PlanLifecycle> = ["active", "expiring", "fulfilled", "ended"];
export interface MembershipStatus {
  status: PlanLifecycle;
  source: "esc" | "pegasus";
  plan_id: string;
  plan_name: string | null;
  term_years: number | null;
  start_date: string | null;
  expire_date: string | null;
  days_to_expire: number | null;
  checks_total: number;
  checks_used: number;
  checks_owed: number;
  owed_labels: string | null;
  owed_until: string | null;
  next_check: string | null;
  next_check_date: string | null;
  renewal_due: boolean;
  member_pricing: boolean;
}
export async function fetchMembershipStatus(custno: string): Promise<MembershipStatus | null> {
  const { data, error } = await supabase.rpc("pegasus_membership_status", { p_custno: custno });
  if (error) throw error;
  return ((data ?? []) as MembershipStatus[])[0] ?? null;
}
export function useMembershipStatus(custno: string | undefined) {
  return useQuery({
    queryKey: ["membership_status", custno],
    enabled: !!custno,
    staleTime: 5 * 60 * 1000,
    queryFn: () => fetchMembershipStatus(custno as string),
  });
}

export interface MembershipCard {
  source: "esc" | "pegasus";
  planName: string;             // "3-Year PLUS Plan" / Pegasus base_name
  /** 'ended' (MEMBERSHIPS-03) = the term is over, a check is still owed — still a member for pricing.
   *  'fulfilled' (MEMBERSHIP-ESC-FULFILLED) = paid through, every check used — still a member, renewal due. */
  status: "active" | "expiring" | "fulfilled" | "ended" | "expired" | "inactive";
  /** The SQL lifecycle behind `status`, when the status function answered. */
  lifecycle: PlanLifecycle | null;
  /** Checks the current term still owes (booked ones included) + the wording for them. */
  checksOwed: number;
  owedLabels: string | null;
  owedUntil: string | null;
  renewalDue: boolean;
  daysToExpire: number | null;
  begDate: string | null;
  expireDate: string | null;
  lastService: string | null;
  /** ESC renew_date — when the agreement last rolled (Seth 2026-08-27). */
  renewDate: string | null;
  memberSinceYear: number | null; // ESC orig_contr — loyalty at a glance
  priceAmount: number | null;
  termLabel: string | null;       // "3-Year term" / "1-Year term"
  autopay: boolean;               // Pegasus plans only
  secondSystem: boolean;          // Pegasus plans only
  /** Plan-wide visit ledger ("1 of 6 used") — from ESC's own counter, mined
   *  by the agreement_detail RPC off the latest dispatch note. NOT per-check:
   *  ESC's per-task "X of Y" numbers are POSITIONS in one shared sequence
   *  (visit 1 = A/C, 2 = furnace, …), not per-type usage counts. */
  planVisits: { used: number; total: number } | null;
  /** The check occupying position used+1 — what's coming next. */
  nextCheck: string | null;
  /** Distinct check types the plan includes ("A/C tune-up", "Furnace tune-up"). */
  includes: string[];
}

export function useMembershipCard(custno: string | undefined) {
  return useQuery({
    queryKey: ["membership_card", custno],
    enabled: !!custno,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<MembershipCard | null> => {
      // MEMBERSHIP-ESC-FULFILLED: the ESC candidates are the active rows plus the
      // one the SQL lifecycle calls 'fulfilled' (inactive in ESC after the final
      // tune-up, still paid through) — same rule as useCustomerAgreementRenewal.
      const [{ data: esc, error: e1 }, { data: peg, error: e2 }, { data: ful }] = await Promise.all([
        supabase
          .from("mirror_serv_agr")
          .select(
            "custno, locno, agrmt_no, agrmt_type, contr_per, contr_amt, dept, orig_contr, beg_date, renew_date, expire_date, last_srvc, next_post_date, sa_inactive, notes",
          )
          .eq("custno", custno!)
          .or(`sa_inactive.eq.0,expire_date.gte.${todayISO()}`)
          .not("expire_date", "is", null)
          .order("expire_date", { ascending: false })
          .limit(6),
        supabase
          .from("pegasus_plan_sale")
          .select("id, base_name, term, total, autopay, second_system, start_date, expire_date")
          .eq("custno", custno!)
          .eq("status", "sold") // MEMBER-TRUTH-01: sold plans only
          .not("expire_date", "is", null)
          .order("expire_date", { ascending: false })
          .limit(1),
        supabase
          .from("v_membership_plan")
          .select("agrmt_no")
          .eq("custno", custno!)
          .eq("source", "esc")
          .eq("status", "fulfilled")
          .order("expire_date", { ascending: false })
          .limit(1),
      ]);
      if (e1) throw e1;
      if (e2) throw e2;
      const fulfilledNo = ((ful ?? [])[0] as { agrmt_no: string } | undefined)?.agrmt_no ?? null;
      type EscRow = {
        custno: string; locno: string; agrmt_no: string; agrmt_type: string | null;
        contr_per: string | null; contr_amt: number | null; orig_contr: string | null;
        beg_date: string | null; renew_date: string | null; expire_date: string | null; last_srvc: string | null;
        sa_inactive: number | null;
      };
      const escRow = ((esc ?? []) as EscRow[]).find((r) => r.sa_inactive === 0 || r.agrmt_no === fulfilledNo);
      const pegRow = (peg ?? [])[0] as
        | {
            id: string; base_name: string | null; term: number | null; total: number | null;
            autopay: boolean | null; second_system: boolean | null;
            start_date: string | null; expire_date: string | null;
          }
        | undefined;
      if (!escRow && !pegRow) return null;
      const usePeg =
        !!pegRow && (!escRow || (pegRow.expire_date ?? "") > (escRow.expire_date ?? ""));

      const t = new Date(todayISO() + "T00:00:00").getTime();
      const daysTo = (iso: string | null) =>
        iso ? Math.round((new Date(iso + "T00:00:00").getTime() - t) / 86400000) : null;
      // MEMBERSHIPS-03: the lifecycle comes from SQL (one rule for every
      // screen); the day-count is only the fallback when it can't answer.
      const life = await fetchMembershipStatus(custno!).catch(() => null);
      const lifeMatches = !!life && ((usePeg && life.source === "pegasus" && life.plan_id === pegRow?.id) || (!usePeg && life.source === "esc" && life.plan_id === escRow?.agrmt_no));
      const statusOf = (days: number | null): MembershipCard["status"] => {
        if (lifeMatches && life) {
          if (life.status === "active" || life.status === "expiring" || life.status === "ended" || life.status === "fulfilled") return life.status;
          if (life.status === "cancelled") return "inactive";
          return "expired";
        }
        return days === null ? "active" : days < 0 ? "expired" : days <= 60 ? "expiring" : "active";
      };
      const fulfilled = lifeMatches && life?.status === "fulfilled";
      const lifeBits = {
        lifecycle: lifeMatches && life ? life.status : null,
        checksOwed: lifeMatches && life ? life.checks_owed : 0,
        owedLabels: lifeMatches && life ? life.owed_labels : null,
        owedUntil: lifeMatches && life ? life.owed_until : null,
        renewalDue: lifeMatches && life ? life.renewal_due : false,
      };

      if (usePeg && pegRow) {
        const days = daysTo(pegRow.expire_date);
        // MEMBERSHIPS-02 P1: a Pegasus plan's visit ledger is its generated
        // agreement_visit rows — used = completed, forfeited = term ended
        // unused (the nightly rule), next = the earliest open check.
        const { data: visits } = await supabase
          .from("agreement_visit")
          .select("task_label, covers_category, status, target_date, completed_date")
          .eq("plan_sale_id", pegRow.id)
          .eq("source", "generated")
          .order("target_date", { ascending: true });
        const vs = (visits ?? []) as { task_label: string | null; covers_category: string | null; status: string; target_date: string | null; completed_date: string | null }[];
        const used = vs.filter((v) => v.status === "completed").length;
        const forfeited = vs.filter((v) => v.status === "forfeited").length;
        const nextOpen = vs.find((v) => !["completed", "forfeited", "skipped", "cancelled"].includes(v.status));
        const lastDone = [...vs].reverse().find((v) => v.status === "completed");
        const month = (iso: string | null) => (iso ? new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "short", year: "numeric" }) : null);
        return {
          source: "pegasus",
          planName: pegRow.base_name?.trim() || "PLUS Plan",
          status: statusOf(days),
          ...lifeBits,
          daysToExpire: days,
          begDate: pegRow.start_date,
          expireDate: pegRow.expire_date,
          lastService: lastDone?.completed_date ?? null,
          renewDate: null,
          memberSinceYear: pegRow.start_date ? new Date(pegRow.start_date + "T00:00:00").getFullYear() : null,
          priceAmount: pegRow.total,
          termLabel: pegRow.term ? `${pegRow.term}-Year term` : null,
          autopay: !!pegRow.autopay,
          secondSystem: !!pegRow.second_system,
          planVisits: vs.length > 0 ? { used, total: vs.length } : null,
          nextCheck: nextOpen ? `${nextOpen.task_label ?? "Tune-up"}${month(nextOpen.target_date) ? ` · ${month(nextOpen.target_date)}` : ""}` : forfeited > 0 ? `${forfeited} unused — forfeited at term end` : null,
          includes: [...new Set(vs.map((v) => v.task_label).filter((x): x is string => !!x))],
        };
      }

      // ESC agreement — task lines (check types + positions) + the
      // agreement_detail RPC's per-TERM tune-up counts (AGR-TUNEUPS-02,
      // 2026-09-01): used = checks COMPLETED this term (a booked-but-not-done
      // check is never "used"), entitled = the plan's own task-line "of Y".
      // The AgrTask lines mix last-done and next-up positions per type, so
      // they can't be summed as usage.
      const [{ data: tasks, error: e3 }, { data: detail }] = await Promise.all([
        supabase
          .from("mirror_agr_task")
          .select("long_desc")
          .eq("custno", custno!)
          .eq("agrmt_no", escRow!.agrmt_no),
        supabase.rpc("agreement_detail", { p_custno: custno, p_locno: null }),
      ]);
      if (e3) throw e3;
      const taskRows = (tasks ?? []) as { long_desc: string | null }[];
      const parsed = taskRows.map((tr) => {
        const raw = (tr.long_desc ?? "").replace(/\s+/g, " ").trim();
        const m = raw.toUpperCase().match(/(\d+)\s+OF\s+(\d+)/);
        return {
          label: detectCheckType(raw) ?? "Tune-up",
          position: m ? parseInt(m[1], 10) : null,
          total: m ? parseInt(m[2], 10) : null,
        };
      });
      const detailAgs = (detail as {
        agreements?: Array<{ agrmt_no: string; tuneups: { used: number | null; entitled: number | null } | null }>;
      } | null)?.agreements ?? [];
      const mined = detailAgs.find((g) => String(g.agrmt_no) === escRow!.agrmt_no)?.tuneups ?? null;
      // MEMBERSHIPS-02 P2: the term's generated visit rows are the ledger when
      // they exist (they do for every active ESC plan since Sept 2026); the
      // mined "X of Y" counter stays the fallback.
      const { data: genRows } = await supabase
        .from("agreement_visit")
        .select("task_label, status, target_date")
        .eq("custno", custno!)
        .eq("agrmt_no", escRow!.agrmt_no)
        .eq("source", "generated")
        .is("plan_sale_id", null)
        .eq("term_start", escRow!.beg_date ?? "1900-01-01")
        .order("target_date", { ascending: true });
      const gen = (genRows ?? []) as { task_label: string | null; status: string; target_date: string | null }[];
      const genOpen = gen.find((g) => !["completed", "forfeited", "skipped", "cancelled", "declined"].includes(g.status));
      const monthOf = (iso: string | null) => (iso ? new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "short", year: "numeric" }) : null);
      const total = gen.length > 0 ? gen.length : (mined?.entitled ?? parsed.find((p) => p.total != null)?.total ?? null);
      // MEMBERSHIP-ESC-FULFILLED: a fulfilled plan has used every check of the
      // term by ESC's own count — the ledger rows (numbered off a different
      // anchor) can still show one unmatched; the plan's word wins here.
      const used = fulfilled && total != null ? total : gen.length > 0 ? gen.filter((g) => g.status === "completed").length : (mined?.used ?? null);
      const planVisits = used != null && total != null ? { used, total } : null;
      // Next check = the earliest owed generated visit; else the task line
      // whose position is used+1 (ESC pre-types the upcoming position).
      const nextCheck =
        fulfilled
          ? null
          : gen.length > 0
          ? (genOpen ? `${genOpen.task_label ?? "Tune-up"}${monthOf(genOpen.target_date) ? ` · ${monthOf(genOpen.target_date)}` : ""}` : null)
          : used != null
            ? (parsed.find((p) => p.position === used + 1)?.label ?? null)
            : null;
      const includes = [...new Set(parsed.map((p) => p.label))];

      // Plan name: "3 Year" -> "3-Year"; brand it PLUS when the task lines
      // carry ESC's PLUS coding (that's Sedgwick's plan family name).
      const per = (escRow!.contr_per ?? "").trim();
      let base =
        per
          ? per.replace(/^(\d+)\s*Year$/i, "$1-Year")
          : /^\d+YR$/i.test(escRow!.agrmt_type ?? "")
            ? `${(escRow!.agrmt_type ?? "").toUpperCase().replace("YR", "")}-Year`
            : (escRow!.agrmt_type ?? "Service");
      const isPlus = taskRows.some((tr) => /\bPLUS\b/i.test(tr.long_desc ?? ""));
      const days = daysTo(escRow!.expire_date);
      return {
        source: "esc",
        planName: `${base}${isPlus ? " PLUS" : ""} Plan`,
        status: statusOf(days),
        ...lifeBits,
        daysToExpire: days,
        begDate: escRow!.beg_date,
        expireDate: escRow!.expire_date,
        lastService: escRow!.last_srvc,
        renewDate: escRow!.renew_date,
        memberSinceYear: escRow!.orig_contr
          ? new Date(escRow!.orig_contr + "T00:00:00").getFullYear()
          : null,
        priceAmount: escRow!.contr_amt,
        termLabel: per ? `${per.replace(/^(\d+)\s*Year$/i, "$1-Year")} term` : null,
        autopay: false,
        secondSystem: false,
        planVisits,
        nextCheck,
        includes,
      };
    },
  });
}

// ----------------------------------------------------------------------------
// AGR-03a — derived visit history for one agreement. Truth comes from work
// PERFORMED, not the hand-typed "X of Y" in AgrTask.long_desc (per
// feedback_derive_dont_flag). A "visit" = a mirror_sales row tagged with this
// agrmt_no that has a DISPATCH (an actual truck-roll); agreement-tagged rows
// WITHOUT a dispatch are billing events (e.g. the annual renewal payment) and
// are not visits. $0 visits are covered (included) tune-ups; $>0 visits had a
// billed repair on top. usedThisTerm counts visits since the current term
// began (renew_date/beg_date). Going forward, live Pegasus will mark visits
// from completed dispatches natively; this hook is the historical-truth read.
// ----------------------------------------------------------------------------
export interface AgreementVisit {
  invoice: string;
  dispatch: string;
  date: string;
  amount: number;
  covered: boolean; // no charge = included tune-up
  type: string | null; // "Furnace tune-up" / "A/C tune-up" / null (unrecognized)
}

// Derive the check type from the dispatch notes — the same "PLUS FRN CK" /
// "PLUS AC CK" codes the techs/CSRs already write (and that AgrTask uses).
// Only label clear tune-up checks (strict mode); repairs (e.g. "No Cool")
// return null so we don't mislabel a billed repair as a covered check.
// EARLIEST phrase wins: "PLUS AC CK,, ACC AT FRN CK" is an A/C check even
// though "FRN CK" appears later in the note (the Mary Gannon fix, 2026-08-18).
function parseVisitType(notes: string): string | null {
  return detectCheckType(notes, { strict: true });
}
export interface AgreementVisitSummary {
  visits: AgreementVisit[]; // recent-first
  usedThisTerm: number;
  lifetimeVisits: number;
}

export function useAgreementVisits(
  custno: string | undefined,
  locno: string | null | undefined,
  agrmtNo: string,
  termStart: string | null,
) {
  return useQuery({
    queryKey: ["agreement_visits", custno, locno ?? "all", agrmtNo],
    enabled: !!custno && !!agrmtNo,
    queryFn: async (): Promise<AgreementVisitSummary> => {
      let q = supabase
        .from("mirror_sales")
        .select("invoice, dispatch, inv_date, inv_amount")
        .eq("custno", custno!)
        .eq("agrmt_no", agrmtNo)
        .order("inv_date", { ascending: false });
      if (locno) q = q.eq("locno", locno);
      const { data, error } = await q;
      if (error) throw error;
      const rows = (data ?? []) as {
        invoice: string;
        dispatch: string | null;
        inv_date: string | null;
        inv_amount: number | null;
      }[];
      // A visit requires a real dispatch + a date. Truthy guard drops both
      // null and empty-string dispatches (billing-only rows).
      const visits: AgreementVisit[] = rows
        .filter((r) => r.dispatch && r.inv_date)
        .map((r) => {
          const amt = Number(r.inv_amount ?? 0);
          return {
            invoice: r.invoice,
            dispatch: r.dispatch as string,
            date: r.inv_date as string,
            amount: amt,
            covered: amt === 0,
            type: null as string | null,
          };
        });

      // Enrich with check type from the dispatch notes (one batched lookup).
      const dispatchNos = Array.from(new Set(visits.map((v) => v.dispatch)));
      if (dispatchNos.length > 0) {
        const { data: disps } = await supabase
          .from("v_dispatch_full")
          .select("dispatch, notes")
          .in("dispatch", dispatchNos);
        const notesByDisp = new Map<string, string>();
        for (const d of (disps ?? []) as { dispatch: string; notes: string | null }[]) {
          if (!notesByDisp.has(d.dispatch)) notesByDisp.set(d.dispatch, d.notes ?? "");
        }
        for (const v of visits) {
          v.type = parseVisitType(notesByDisp.get(v.dispatch) ?? "");
        }
      }

      const usedThisTerm = termStart
        ? visits.filter((v) => v.date >= termStart).length
        : 0;
      return { visits, usedThisTerm, lifetimeVisits: visits.length };
    },
    staleTime: 60 * 1000,
  });
}

// ----------------------------------------------------------------------------
// DEC-23 — derived UPCOMING agreement visits. ESC posts future-dated "Serv Agr"
// dispatches (pseudo-tech 9999) to represent the next tune-up; Pegasus DERIVES
// it from the plan's recurrence schedule (mirror_agr_recur, joined via
// serv_agr.recur_sched_id) instead. No ghost ticket — the upcoming visit is
// computed from the plan and surfaced where the dispatcher needs it (e.g. when
// a PLUS customer calls). Month granularity: each check recurs in a fixed month
// ("first Sunday of September"); we show the next future occurrence of that
// month. Self-contained (two small reads) so it needs no change to the
// agreement view types.
// ----------------------------------------------------------------------------
const RECUR_MONTHS: Record<string, number> = {
  january: 0, february: 1, march: 2, april: 3, may: 4, june: 5, july: 6,
  august: 7, september: 8, october: 9, november: 10, december: 11,
  jan: 0, feb: 1, mar: 2, apr: 3, jun: 5, jul: 6, aug: 7, sep: 8, sept: 8, oct: 9, nov: 10, dec: 11,
};
const RECUR_MONTH_LABEL = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Sedgwick's standard plan task codes: 005 = furnace check (fall), 006 = A/C
// check (spring). Unknown codes fall back to a generic label (refine later).
function recurCheckLabel(task: string | null): string {
  const t = (task ?? "").trim();
  if (t === "005") return "Furnace tune-up";
  if (t === "006") return "A/C tune-up";
  return "Plan visit";
}
// Month of the recurrence — prefer the structured month_of_year, else parse the
// human recur_desc ("Every Dec", "The first Sunday of September").
function recurMonth(monthOfYear: string | null, recurDesc: string | null): number | null {
  const m = (monthOfYear ?? "").trim().toLowerCase();
  if (m && m in RECUR_MONTHS) return RECUR_MONTHS[m];
  const d = (recurDesc ?? "").toLowerCase();
  for (const name of Object.keys(RECUR_MONTHS)) {
    if (new RegExp(`\\b${name}\\b`).test(d)) return RECUR_MONTHS[name];
  }
  return null;
}

export interface UpcomingVisit {
  label: string;
  monthIndex: number;
  year: number;
  when: string; // "~Sep 2026"
}

export function useAgreementSchedule(
  custno: string | undefined,
  locno: string | null | undefined,
  agrmtNo: string,
) {
  return useQuery({
    queryKey: ["agreement_schedule", custno, locno ?? "all", agrmtNo],
    enabled: !!custno && !!agrmtNo,
    queryFn: async (): Promise<UpcomingVisit[]> => {
      // 1) the agreement's recurrence schedule id
      let sq = supabase
        .from("mirror_serv_agr")
        .select("recur_sched_id")
        .eq("custno", custno!)
        .eq("agrmt_no", agrmtNo);
      if (locno) sq = sq.eq("locno", locno);
      const { data: sa, error: se } = await sq.limit(1);
      if (se) throw se;
      const recurId = ((sa ?? [])[0] as { recur_sched_id: string | null } | undefined)?.recur_sched_id;
      if (!recurId) return [];

      // 2) the schedule's recurring tasks
      const { data, error } = await supabase
        .from("mirror_agr_recur")
        .select("task, recur_desc, month_of_year")
        .eq("trans_id", recurId);
      if (error) throw error;
      const rows = (data ?? []) as { task: string | null; recur_desc: string | null; month_of_year: string | null }[];

      const now = new Date();
      const curY = now.getFullYear();
      const curM = now.getMonth();
      const out: UpcomingVisit[] = [];
      const seen = new Set<string>();
      for (const r of rows) {
        const mi = recurMonth(r.month_of_year, r.recur_desc);
        if (mi === null) continue;
        const year = mi >= curM ? curY : curY + 1; // this year if the month hasn't passed
        const label = recurCheckLabel(r.task);
        const key = `${label}|${mi}|${year}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({ label, monthIndex: mi, year, when: `~${RECUR_MONTH_LABEL[mi]} ${year}` });
      }
      out.sort((a, b) => a.year - b.year || a.monthIndex - b.monthIndex);
      return out;
    },
    staleTime: 5 * 60 * 1000,
  });
}

// ----------------------------------------------------------------------------
// Active-coverage set — the agrmt_no values that are CURRENTLY in force for a
// customer (sa_inactive=0 AND not past expire_date). Used to stop the
// equipment "On agreement" tag from trusting ESC's stale manual ser_agr_no
// flag (which nobody unchecks when an agreement lapses — see Julie 0001071).
// ----------------------------------------------------------------------------
export function useActiveAgreementNumbers(custno: string | undefined) {
  return useQuery({
    queryKey: ["active_agreement_nos", custno],
    enabled: !!custno,
    queryFn: async (): Promise<Set<string>> => {
      const { data, error } = await supabase
        .from("mirror_serv_agr")
        .select("agrmt_no, expire_date")
        .eq("custno", custno!)
        .eq("sa_inactive", 0)
        .gte("expire_date", todayISO());
      if (error) throw error;
      return new Set(
        ((data ?? []) as { agrmt_no: string }[]).map((r) => r.agrmt_no),
      );
    },
    staleTime: 5 * 60 * 1000,
  });
}

// ----------------------------------------------------------------------------
// AGR-03b — the editable engine. Pegasus writes ONLY human decisions (the
// override layer) to agreement_visit; completed/owed stay derived. An override
// targets one check instance = (custno, locno, agrmt_no, task_label,
// service-year start). status: deferred | substituted | waived | carried_over.
// Every write carries who/when/why (audit). NOT an ESC write — Supabase only
// (clear of DEC-22). No deletes (changing an override updates the row).
// ----------------------------------------------------------------------------
export type OverrideStatus = "deferred" | "substituted" | "waived" | "carried_over";

export interface VisitOverride {
  id: string;
  task_label: string | null;
  term_start: string | null; // = the service-year start (the instance period)
  status: OverrideStatus;
  deferred_to: string | null;
  covers_category: string | null; // substitute target
  reason: string | null;
  updated_by: string | null;
  updated_at: string | null;
}

export function useAgreementOverrides(
  custno: string | undefined,
  locno: string | null | undefined,
  agrmtNo: string,
) {
  return useQuery({
    queryKey: ["agreement_overrides", custno, locno ?? "all", agrmtNo],
    enabled: !!custno && !!agrmtNo,
    queryFn: async (): Promise<VisitOverride[]> => {
      let q = supabase
        .from("agreement_visit")
        .select("id, task_label, term_start, status, deferred_to, covers_category, reason, updated_by, updated_at")
        .eq("custno", custno!)
        .eq("agrmt_no", agrmtNo)
        .eq("source", "manual");
      if (locno) q = q.eq("locno", locno);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as VisitOverride[];
    },
    staleTime: 30 * 1000,
  });
}

export interface SetOverrideInput {
  taskLabel: string;
  serviceYearStart: string;
  status: OverrideStatus;
  reason: string;
  deferredTo?: string | null;
  coversCategory?: string | null;
}

export function useSetVisitOverride(
  custno: string,
  locno: string,
  agrmtNo: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SetOverrideInput) => {
      const { data: auth } = await supabase.auth.getUser();
      const updated_by = auth?.user?.email ?? auth?.user?.id ?? null;
      const row = {
        custno,
        locno,
        agrmt_no: agrmtNo,
        task_label: input.taskLabel,
        term_start: input.serviceYearStart,
        status: input.status,
        deferred_to: input.deferredTo ?? null,
        covers_category: input.coversCategory ?? null,
        reason: input.reason || null,
        source: "manual",
        updated_by,
        updated_at: new Date().toISOString(),
      };
      // One override per check instance: update if it exists, else insert
      // (never delete — changing a decision rewrites the same row).
      const { data: existing } = await supabase
        .from("agreement_visit")
        .select("id")
        .eq("custno", custno)
        .eq("locno", locno)
        .eq("agrmt_no", agrmtNo)
        .eq("task_label", input.taskLabel)
        .eq("term_start", input.serviceYearStart)
        .eq("source", "manual")
        .limit(1);
      if (existing && existing.length > 0) {
        const { error } = await supabase
          .from("agreement_visit")
          .update(row)
          .eq("id", (existing[0] as { id: string }).id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("agreement_visit").insert(row);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agreement_overrides", custno, locno, agrmtNo] });
      qc.invalidateQueries({ queryKey: ["agreement_overrides", custno, "all", agrmtNo] });
    },
  });
}

// Clear an override → the check returns to its derived state (Reschedule/Skip
// etc. undone). Removes the single override row for this check instance.
export function useClearVisitOverride(
  custno: string,
  locno: string,
  agrmtNo: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { taskLabel: string; serviceYearStart: string }) => {
      const { error } = await supabase
        .from("agreement_visit")
        .delete()
        .eq("custno", custno)
        .eq("locno", locno)
        .eq("agrmt_no", agrmtNo)
        .eq("task_label", input.taskLabel)
        .eq("term_start", input.serviceYearStart)
        .eq("source", "manual");
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agreement_overrides", custno, locno, agrmtNo] });
      qc.invalidateQueries({ queryKey: ["agreement_overrides", custno, "all", agrmtNo] });
    },
  });
}

// AGR-06 win-back list — agreements that lapsed in the last 12 months with no
// current active coverage at that location.
export interface LapsedAgreement {
  custno: string;
  locno: string;
  agrmt_no: string;
  agrmt_type: string | null;
  contr_per: string | null;
  contr_amt: number | null;
  expire_date: string;
  last_srvc: string | null;
  customer_fullname: string | null;
  city: string | null;
  days_lapsed: number;
}

export function useLapsedAgreements() {
  return useQuery({
    queryKey: ["lapsed_agreements"],
    queryFn: async (): Promise<LapsedAgreement[]> => {
      const rows = await pagedAll<Omit<LapsedAgreement, "customer_fullname" | "days_lapsed">>(
        () =>
          supabase
            .from("v_lapsed_agreements")
            .select("custno, locno, agrmt_no, agrmt_type, contr_per, contr_amt, expire_date, last_srvc")
            .order("expire_date", { ascending: false })
            .order("agrmt_no", { ascending: true }),
      );
      if (rows.length === 0) return [];
      const custnos = Array.from(new Set(rows.map((r) => r.custno)));
      const nameByCust = await fetchNameMap(custnos);
      const cityMap = await fetchCityMap(custnos);
      const t = new Date(todayISO() + "T00:00:00").getTime();
      return rows.map((r) => ({
        ...r,
        customer_fullname: nameByCust.get(r.custno) ?? null,
        city: cityMap.get(`${r.custno}|${r.locno}`) ?? null,
        days_lapsed: Math.round((t - new Date(r.expire_date + "T00:00:00").getTime()) / 86400000),
      }));
    },
    staleTime: 5 * 60 * 1000,
  });
}

// AGR-06 owed-but-unscheduled — active members owed a visit with nothing booked.
export interface OwedAgreement {
  custno: string;
  locno: string;
  agrmt_no: string;
  agrmt_type: string | null;
  contr_per: string | null;
  contr_amt: number | null;
  expire_date: string;
  last_srvc: string | null;
  customer_fullname: string | null;
  city: string | null;
}

export function useOwedUnscheduled() {
  return useQuery({
    queryKey: ["owed_unscheduled"],
    queryFn: async (): Promise<OwedAgreement[]> => {
      const rows = await pagedAll<Omit<OwedAgreement, "customer_fullname">>(
        () =>
          supabase
            .from("v_owed_unscheduled")
            .select("custno, locno, agrmt_no, agrmt_type, contr_per, contr_amt, expire_date, last_srvc")
            .order("last_srvc", { ascending: true, nullsFirst: true })
            .order("agrmt_no", { ascending: true }),
      );
      if (rows.length === 0) return [];
      const custnos = Array.from(new Set(rows.map((r) => r.custno)));
      const nameByCust = await fetchNameMap(custnos);
      const cityMap = await fetchCityMap(custnos);
      return rows.map((r) => ({
        ...r,
        customer_fullname: nameByCust.get(r.custno) ?? null,
        city: cityMap.get(`${r.custno}|${r.locno}`) ?? null,
      }));
    },
    staleTime: 5 * 60 * 1000,
  });
}

// ----------------------------------------------------------------------------
// DEC-23 — UPCOMING agreement visits worklist. The proactive complement to
// Owed-visits: who's coming due for a tune-up, so we schedule/contact them
// BEFORE it's owed. Driven by serv_agr.next_post_date (ESC's computed next
// agreement-visit date) — populated for ALL active members, unlike the
// recurrence join (~half). Replaces ESC's future-dated 9999 "Serv Agr" report.
// Visits cluster seasonally (furnace ~Sept, A/C ~May), so this reads as a
// seasonal campaign list, not a rolling daily queue.
// ----------------------------------------------------------------------------
export interface UpcomingVisitRow {
  custno: string;
  locno: string;
  agrmt_no: string;
  agrmt_type: string | null;
  contr_per: string | null;
  contr_amt: number | null;
  next_post_date: string;
  expire_date: string | null;
  customer_fullname: string | null;
  city: string | null;
  days_until: number;
  visitLabel: string; // inferred from the posting month (Sep→furnace, May→A/C)
}

// Cheap check-type inference from the posting month — furnace checks anchor to
// fall (~September), A/C to spring (~May) on the standard plan. Avoids a second
// query; the precise per-check type comes with the recurrence join later.
function visitLabelFromDate(iso: string): string {
  const m = parseInt(iso.slice(5, 7), 10);
  if (m >= 8 && m <= 11) return "Furnace tune-up"; // Aug–Nov
  if (m >= 3 && m <= 6) return "A/C tune-up"; // Mar–Jun
  return "Plan visit";
}

export function useUpcomingVisits(windowDays: number | null = 180) {
  return useQuery({
    queryKey: ["upcoming_agreement_visits", windowDays],
    queryFn: async (): Promise<UpcomingVisitRow[]> => {
      const today = todayISO();
      // Query is built FRESH per page inside pagedAll (builders are mutable).
      const buildQuery = () => {
        let qq = supabase
          .from("mirror_serv_agr")
          .select("custno, locno, agrmt_no, agrmt_type, contr_per, contr_amt, next_post_date, expire_date")
          .eq("sa_inactive", 0)
          .gte("expire_date", today)
          .gte("next_post_date", today);
        if (windowDays != null) qq = qq.lte("next_post_date", addDaysISO(windowDays));
        return qq
          .order("next_post_date", { ascending: true })
          .order("agrmt_no", { ascending: true });
      };
      const rows = await pagedAll<
        Omit<UpcomingVisitRow, "customer_fullname" | "city" | "days_until" | "visitLabel">
      >(buildQuery);
      if (rows.length === 0) return [];
      const custnos = Array.from(new Set(rows.map((r) => r.custno)));
      const nameByCust = await fetchNameMap(custnos);
      const cityMap = await fetchCityMap(custnos);
      const t = new Date(today + "T00:00:00").getTime();
      return rows.map((r) => ({
        ...r,
        customer_fullname: nameByCust.get(r.custno) ?? null,
        city: cityMap.get(`${r.custno}|${r.locno}`) ?? null,
        days_until: Math.round((new Date(r.next_post_date + "T00:00:00").getTime() - t) / 86400000),
        visitLabel: visitLabelFromDate(r.next_post_date),
      }));
    },
    staleTime: 5 * 60 * 1000,
  });
}

// AGR-06 command-center KPIs (one row from v_agreement_kpis).
export interface AgreementKpis {
  active_agreements: number;
  members: number;
  total_contract_value: number;
  annualized_recurring_rev: number;
  expiring_90d: number;
  value_expiring_90d: number;
  lapsed_12mo: number;
  retention_est: number;
}

export function useAgreementKpis() {
  return useQuery({
    queryKey: ["agreement_kpis"],
    queryFn: async (): Promise<AgreementKpis | null> => {
      const { data, error } = await supabase
        .from("v_agreement_kpis")
        .select("*")
        .limit(1);
      if (error) throw error;
      return ((data ?? [])[0] as AgreementKpis) ?? null;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useExpiringAgreements(windowDays: number) {
  return useQuery({
    queryKey: ["expiring_agreements", windowDays],
    queryFn: async (): Promise<ExpiringAgreement[]> => {
      const today = todayISO();
      const end = addDaysISO(windowDays);
      const rows = await pagedAll<Omit<ExpiringAgreement, "customer_fullname" | "days_left">>(
        () =>
          supabase
            .from("mirror_serv_agr")
            .select("custno, locno, agrmt_no, agrmt_type, contr_per, contr_amt, expire_date, renew_date, last_srvc")
            .eq("sa_inactive", 0)
            .gte("expire_date", today)
            .lte("expire_date", end)
            .order("expire_date", { ascending: true })
            .order("agrmt_no", { ascending: true }),
      );
      if (rows.length === 0) return [];

      const custnos = Array.from(new Set(rows.map((r) => r.custno)));
      const nameByCust = await fetchNameMap(custnos);

      const cityMap = await fetchCityMap(custnos);
      const t = new Date(today + "T00:00:00").getTime();
      return rows.map((r) => ({
        ...r,
        customer_fullname: nameByCust.get(r.custno) ?? null,
        city: cityMap.get(`${r.custno}|${r.locno}`) ?? null,
        days_left: Math.round((new Date(r.expire_date + "T00:00:00").getTime() - t) / 86400000),
      }));
    },
    staleTime: 5 * 60 * 1000,
  });
}
